'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

// ─── FOUNDER VOICE MOMENTS ────────────────────────────────────────────────────

export type FounderVoiceKey =
  | 'first_seed_planted' |'first_observation_completed' |'first_mizan_imbalance' |'first_irl_mrg_bridge' |'first_alhamdulilah_harvest' |'first_community_share' |'garden_exit'
  | 'first_observation_garden' | 'first_mizan_imbalance_garden' | 'first_amanah_quest';

interface FounderVoiceMoment {
  key: FounderVoiceKey;
  text: string;
  subtext?: string;
}

const FOUNDER_VOICE_MOMENTS: Record<FounderVoiceKey, FounderVoiceMoment> = {
  first_seed_planted: {
    key: 'first_seed_planted',
    text: 'A structure formed reflecting on sample examinations, commentary, and field notes. I\'d like to walk with you.',
  },
  first_observation_completed: {
    key: 'first_observation_completed',
    text: 'Go from aware to conscious, then act accordingly.',
  },
  first_mizan_imbalance: {
    key: 'first_mizan_imbalance',
    text: 'Aware of a good to do, encouraged to be conscious of how.',
  },
  first_irl_mrg_bridge: {
    key: 'first_irl_mrg_bridge',
    text: 'We\'re offered this clear direction from the Sovereign of us and all that we\'re entrusted with for a brief period.',
  },
  first_alhamdulilah_harvest: {
    key: 'first_alhamdulilah_harvest',
    text: 'We aren\'t some spontaneous occurrence. Reflect on the perfect systems and design of creation around us.',
  },
  first_community_share: {
    key: 'first_community_share',
    text: 'Those who submit to revelation call to it.',
  },
  garden_exit: {
    key: 'garden_exit',
    text: 'You\'ve been entrusted with what you\'ve seen.',
    subtext: 'Go tend it.',
  },
  // ─── THREE SPECIFIC JOURNEY MOMENTS (FounderThread) ──────────────────────
  first_observation_garden: {
    key: 'first_observation_garden',
    text: 'I wrote that as an introduction to the Deen — submission, peace, connection.',
    subtext: 'There is no better introduction to the way of life than the Quran itself.',
  },
  first_mizan_imbalance_garden: {
    key: 'first_mizan_imbalance_garden',
    text: 'We are tasked with not transgressing in the balance, recognizing the signs in creation alongside the signs in revelation, and following the guidance.',
    subtext: 'Open Letter · Section 2: Caretaker',
  },
  first_amanah_quest: {
    key: 'first_amanah_quest',
    text: 'A test to see which of us is excellent in work with the trust that we are granted care over.',
    subtext: 'Open Letter · Section 2: Caretaker',
  },
};

// ─── STORAGE HELPERS ──────────────────────────────────────────────────────────

function hasFounderVoiceFired(key: FounderVoiceKey): boolean {
  if (typeof window === 'undefined') return true;
  return !!localStorage.getItem(`haal_founder_voice_${key}`);
}

function markFounderVoiceFired(key: FounderVoiceKey): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(`haal_founder_voice_${key}`, 'true');
}

export function triggerFounderVoice(key: FounderVoiceKey): boolean {
  if (hasFounderVoiceFired(key)) return false;
  markFounderVoiceFired(key);
  return true;
}

// ─── FOUNDER VOICE COMPONENT ─────────────────────────────────────────────────

interface FounderVoiceProps {
  voiceKey: FounderVoiceKey | null;
  onDismiss: () => void;
}

export function FounderVoice({ voiceKey, onDismiss }: FounderVoiceProps) {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!voiceKey) return;
    setVisible(true);
    setFading(false);
    const t = setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        setVisible(false);
        onDismiss();
      }, 600);
    }, 6000);
    return () => clearTimeout(t);
  }, [voiceKey, onDismiss]);

  if (!visible || !voiceKey) return null;

  const moment = FOUNDER_VOICE_MOMENTS[voiceKey];

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 80,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 8000,
        maxWidth: 560,
        width: 'calc(100vw - 48px)',
        opacity: fading ? 0 : 1,
        transition: 'opacity 0.6s ease',
        animation: fading ? undefined : 'founderVoiceIn 0.5s ease',
      }}
    >
      <div
        style={{
          background: 'rgba(6, 10, 14, 0.96)',
          border: '1px solid rgba(201,168,76,0.4)',
          borderLeft: '3px solid #c9a84c',
          borderRadius: 12,
          padding: '20px 24px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,168,76,0.08)',
        }}
      >
        {/* Quote text */}
        <p
          style={{
            fontFamily: "'Amiri', 'Georgia', serif",
            fontSize: 16,
            color: '#c9a84c',
            lineHeight: 1.75,
            marginBottom: moment.subtext ? 4 : 12,
            textAlign: 'center',
          }}
        >
          &ldquo;{moment.text}&rdquo;
        </p>
        {moment.subtext && (
          <p
            style={{
              fontFamily: "'Amiri', 'Georgia', serif",
              fontSize: 14,
              color: 'rgba(201,168,76,0.7)',
              lineHeight: 1.6,
              marginBottom: 12,
              textAlign: 'center',
              fontStyle: 'italic',
            }}
          >
            {moment.subtext}
          </p>
        )}
        {/* Attribution */}
        <div style={{ textAlign: 'center', borderTop: '1px solid rgba(201,168,76,0.12)', paddingTop: 10 }}>
          <p
            style={{
              fontFamily: 'monospace',
              fontSize: 9,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(201,168,76,0.4)',
              marginBottom: 6,
            }}
          >
            — Open Letter to Imprisoned Caretakers
          </p>
          <Link
            href="/open-letter"
            style={{
              fontFamily: 'monospace',
              fontSize: 9,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'rgba(201,168,76,0.5)',
              textDecoration: 'none',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#c9a84c'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(201,168,76,0.5)'; }}
          >
            ◈ Read the letter →
          </Link>
        </div>
        {/* Dismiss */}
        <button
          onClick={() => { setFading(true); setTimeout(() => { setVisible(false); onDismiss(); }, 600); }}
          style={{
            position: 'absolute',
            top: 10,
            right: 12,
            background: 'transparent',
            border: 'none',
            color: 'rgba(201,168,76,0.3)',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
      <style>{`
        @keyframes founderVoiceIn {
          from { opacity: 0; transform: translateX(-50%) translateY(12px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ─── GARDEN EXIT OVERLAY ──────────────────────────────────────────────────────

export function GardenExitOverlay() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkAndShow = () => {
      const today = new Date().toDateString();
      const lastShown = localStorage.getItem('haal_exit_voice_date');
      if (lastShown === today) return;
      localStorage.setItem('haal_exit_voice_date', today);
      setVisible(true);
      setTimeout(() => setVisible(false), 3400);
    };

    const handleBlur = () => checkAndShow();
    const handleBeforeUnload = () => checkAndShow();

    window.addEventListener('blur', handleBlur);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(6,10,14,0.7)',
        animation: 'exitOverlayIn 0.4s ease',
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 440, padding: '0 24px' }}>
        <p
          style={{
            fontFamily: "'Amiri', 'Georgia', serif",
            fontSize: 'clamp(20px, 4vw, 28px)',
            color: '#c9a84c',
            lineHeight: 1.6,
            marginBottom: 8,
          }}
        >
          You&apos;ve been entrusted with what you&apos;ve seen.
        </p>
        <p
          style={{
            fontFamily: "'Amiri', 'Georgia', serif",
            fontSize: 'clamp(14px, 3vw, 18px)',
            color: 'rgba(201,168,76,0.6)',
            fontStyle: 'italic',
            marginBottom: 20,
          }}
        >
          Go tend it.
        </p>
        <p
          style={{
            fontFamily: "'Amiri', serif",
            fontSize: 18,
            color: 'rgba(201,168,76,0.4)',
            direction: 'rtl',
            marginBottom: 8,
          }}
        >
          وَاللهُ مِن وَرَائِهِم مُّحِيطٌ
        </p>
        <p
          style={{
            fontFamily: 'monospace',
            fontSize: 9,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'rgba(201,168,76,0.25)',
          }}
        >
          Quran 85:20
        </p>
      </div>
      <style>{`
        @keyframes exitOverlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ─── FOUNDER VOICE MANAGER (hook) ────────────────────────────────────────────

export function useFounderVoice() {
  const [activeVoice, setActiveVoice] = useState<FounderVoiceKey | null>(null);

  const fire = useCallback((key: FounderVoiceKey) => {
    if (hasFounderVoiceFired(key)) return;
    markFounderVoiceFired(key);
    setActiveVoice(key);
  }, []);

  const dismiss = useCallback(() => setActiveVoice(null), []);

  return { activeVoice, fire, dismiss };
}
